/**
 * saleHandoff.ts — JOB-SOLD-01 (Seth 2026-09-28: "The tech sells a job in the field on the mobile app. He puts
 * together a proposal, takes pictures of the exterior and interior of the home for the measurements to be done
 * by someone in the office for the load calcs … the 'sold' tab should feel like it has what the tech originally
 * sold and the pictures they uploaded and everything that would be needed to see the scope and do the load calc.
 * Then we verify and enter the proper equipment based on the load calc").
 *
 * The data behind the Sold stage and the phone's packet:
 *   · the photo checklist + site facts the tech is asked for (Settings → Sold Jobs & Load Calc)
 *   · the packet the phone saves as the tech goes (pegasus_job.sale_packet; pegasus_quote.prep_packet before a yes)
 *   · the load calc: a report's numbers read off the file by the AI, or typed, or "not needed" + why
 *   · the Equipment Check sign-off, tied to the exact units it checked (change a unit → it asks again)
 *
 * Competitor frame: HubSpot can require fields before a deal closes and carries the deal's files into the
 * project (adapted: the phone lists what's missing but never blocks the sale — the office sees it here);
 * ServiceTitan re-picks items when booking the install (rejected: the sold units carry over, frozen beside
 * what's on the job now); an install lead checks every sold job before the work (adopted: the sign-off).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useOrgSettingJson } from "@/lib/orgSettings";
import { staffFirst, useStaffNames } from "@/lib/staffNames";
import type { PegasusFile } from "@/features/files/filesCore";
import { MANUAL_S, type SizingRules } from "./capacity";
import type { Job, JobEquipment } from "./jobs";

/* ── Settings ──────────────────────────────────────────────────────────────── */

export interface SalePhotoSlot { label: string; required: boolean; hint?: string }
export const DEFAULT_SALE_PHOTOS: SalePhotoSlot[] = [
  { label: "Front of the House", required: true, hint: "The whole side, corner to corner" },
  { label: "Back of the House", required: true },
  { label: "Left Side", required: true },
  { label: "Right Side", required: true },
  { label: "Existing Furnace", required: true },
  { label: "Furnace Data Tag", required: true, hint: "Close enough to read the model" },
  { label: "Outdoor Unit", required: false },
  { label: "Outdoor Unit Data Tag", required: false },
  { label: "Electrical Panel", required: true },
  { label: "Venting / Chimney", required: false },
  { label: "Attic Insulation", required: false, hint: "A tape measure in the insulation shows the depth" },
  { label: "A Typical Window", required: false },
];
/** The caption a checklist photo carries — how the list knows which slot a photo fills. */
export const siteCaption = (label: string) => `Site — ${label}`;
export function useSalePhotoSlots() {
  return useOrgSettingJson<SalePhotoSlot[]>("sale_photo_slots", DEFAULT_SALE_PHOTOS);
}

export type FactKind = "text" | "number" | "choice";
export interface SiteFact {
  key: string;
  label: string;
  kind: FactKind;
  options?: string[];
  placeholder?: string;
  unit?: string;
  required?: boolean;
}
export const DEFAULT_SITE_FACTS: SiteFact[] = [
  { key: "sqft", label: "Square Feet (Above Ground)", kind: "number", unit: "sq ft", placeholder: "2,400", required: true },
  { key: "basement", label: "Basement", kind: "choice", options: ["Finished", "Part Finished", "Unfinished", "Crawlspace", "Slab"], required: true },
  { key: "stories", label: "Stories", kind: "choice", options: ["1", "1½", "2", "3", "Split-Level"], required: true },
  { key: "year_built", label: "Year Built", kind: "number", placeholder: "1994" },
  { key: "windows", label: "Windows", kind: "choice", options: ["Newer", "Older", "Mixed", "Single-Pane"], required: true },
  { key: "insulation", label: "Attic Insulation", kind: "choice", options: ["Good", "Fair", "Poor", "Can't See"] },
  { key: "existing_heat", label: "Existing Furnace Size", kind: "text", placeholder: "100K 80% — off the tag", required: true },
  { key: "existing_cool", label: "Existing AC Size", kind: "text", placeholder: "3 Ton" },
  { key: "comfort", label: "Comfort Complaints", kind: "text", placeholder: "Upstairs hot in summer, back bedroom cold" },
  { key: "filter", label: "Filter Size", kind: "text", placeholder: "16x25x4" },
];
export function useSiteFacts() {
  return useOrgSettingJson<SiteFact[]>("sale_site_facts", DEFAULT_SITE_FACTS);
}
/** A clean key from a label ("Crawl Space Height" → "crawl_space_height"). */
export const factKey = (label: string) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 40) || "fact";

export interface LoadCalcRules extends SizingRules {
  /** The load-calc program the office uses — a link on the Sold stage. */
  tool_label: string;
  tool_url: string;
  /** Confirming the equipment needs a load calc (or a "not needed" + why). */
  required: boolean;
}
export const DEFAULT_LOAD_CALC_RULES: LoadCalcRules = {
  ...MANUAL_S, tool_label: "CoolCalc", tool_url: "https://www.coolcalc.com/", required: true,
};
export function useLoadCalcRules() {
  const q = useOrgSettingJson<Partial<LoadCalcRules>>("load_calc_rules", DEFAULT_LOAD_CALC_RULES);
  const v = { ...DEFAULT_LOAD_CALC_RULES, ...(q.data ?? {}) };
  return { ...q, rules: v };
}

/* ── The packet the phone sends ───────────────────────────────────────────── */

export interface SalePacket {
  facts?: Record<string, string>;
  notes?: string;
  /** Utility accounts for the rebates: electric / gas utility + account numbers. */
  utility?: { eu?: string; ea?: string; gu?: string; ga?: string };
  /** The day the customer would like the install (the office confirms). */
  pref_day?: string | null;
  /** Stamped by Send to the Office. */
  sent_at?: string | null;
  sent_by?: string | null;
  tech?: string | null;
  from_call?: string | null;
  /** Filled before the yes (on the proposal) and carried into the job. */
  from_prep?: boolean;
  updated_at?: string | null;
}
export const hasPacket = (p: SalePacket | null | undefined) =>
  !!p && (!!p.sent_at || Object.values(p.facts ?? {}).some((v) => (v ?? "").trim()) || !!(p.notes ?? "").trim());

/** Merge a change into the packet on the job (or on the proposal before it's sold). Reads the latest copy first
 *  so a slow phone never erases what it saved a second ago. */
export async function savePacket(target: { jobId?: string | null; quoteId?: string | null }, patch: Partial<SalePacket>): Promise<SalePacket> {
  const now = new Date().toISOString();
  if (target.jobId) {
    const { data } = await supabase.from("pegasus_job").select("sale_packet").eq("id", target.jobId).maybeSingle();
    const cur = ((data as { sale_packet: SalePacket | null } | null)?.sale_packet ?? {}) as SalePacket;
    const next: SalePacket = { ...cur, ...patch, facts: { ...(cur.facts ?? {}), ...(patch.facts ?? {}) }, updated_at: now };
    const { error } = await supabase.from("pegasus_job").update({ sale_packet: next, updated_at: now }).eq("id", target.jobId);
    if (error) throw error;
    return next;
  }
  if (target.quoteId) {
    const { data } = await supabase.from("pegasus_quote").select("prep_packet").eq("id", target.quoteId).maybeSingle();
    const cur = ((data as { prep_packet: SalePacket | null } | null)?.prep_packet ?? {}) as SalePacket;
    const next: SalePacket = { ...cur, ...patch, facts: { ...(cur.facts ?? {}), ...(patch.facts ?? {}) }, updated_at: now };
    const { error } = await supabase.from("pegasus_quote").update({ prep_packet: next, updated_at: now }).eq("id", target.quoteId);
    if (error) throw error;
    return next;
  }
  throw new Error("Nothing to save the packet on.");
}

/** The photos the sale produced: checklist photos, the older "Install site" photos, and anything else the tech
 *  took before the install crew (the crew's own Before / After photos stay on the Installed stage). */
export function salePhotos(files: PegasusFile[] | undefined) {
  const photos = (files ?? []).filter((f) => f.kind === "photo" && !f.archived);
  const slot = (f: PegasusFile) => /^Site — /.test(f.caption ?? "");
  const crew = (f: PegasusFile) => /^(before|after)\b/i.test(f.caption ?? "");
  const other = photos.filter((f) => !slot(f) && !crew(f) && (f.origin === "tech" || /^install site/i.test(f.caption ?? "")));
  return { slotted: photos.filter(slot), other };
}

/* ── The load calc ────────────────────────────────────────────────────────── */

/** What the AI reads off a load calc report (classify-file v4). */
export interface AiLoadCalc {
  heat_loss_btuh: number | null;
  heat_gain_btuh: number | null;
  sensible_btuh?: number | null;
  latent_btuh?: number | null;
  area_sqft?: number | null;
  design_heat_f?: number | null;
  design_cool_f?: number | null;
  software?: string | null;
  method?: string | null;
}

/** Stored on the job: typed numbers, or "not needed" + why. */
export interface LoadCalcRecord {
  heat_btu?: number | null;
  cool_btu?: number | null;
  sqft?: number | null;
  not_needed?: boolean;
  reason?: string | null;
  by?: string | null;
  at?: string | null;
}

export interface EffectiveLoad {
  heat: number | null;
  cool: number | null;
  sqft: number | null;
  source: "typed" | "file" | "not_needed" | "none";
  file?: PegasusFile;
  reason?: string | null;
  by?: string | null;
  at?: string | null;
  /** A report was just added and the AI is still reading it. */
  reading: boolean;
  /** Every load calc report on the job, newest first. */
  reports: PegasusFile[];
}

const aiLoad = (f: PegasusFile) => (f.ai_data as { load_calc?: AiLoadCalc } | null | undefined)?.load_calc ?? null;

/** Typed numbers win; else the newest report's numbers; else nothing (or "not needed"). */
export function effectiveLoad(job: Pick<Job, "load_calc">, files: PegasusFile[] | undefined): EffectiveLoad {
  const rec = (job.load_calc ?? null) as LoadCalcRecord | null;
  const reports = (files ?? []).filter((f) => !f.archived && (f.doc_tag === "load_calc" || (!f.doc_tag && f.ai_tag === "load_calc")))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const reading = reports.some((f) => !f.ai_at && Date.now() - new Date(f.created_at).getTime() < 3 * 60 * 1000);
  const withNums = reports.find((f) => { const l = aiLoad(f); return !!l && (!!l.heat_loss_btuh || !!l.heat_gain_btuh); });
  if (rec?.not_needed) return { heat: null, cool: null, sqft: null, source: "not_needed", reason: rec.reason ?? null, by: rec.by, at: rec.at, reading, reports };
  if (rec && (rec.heat_btu || rec.cool_btu)) {
    return { heat: rec.heat_btu ?? null, cool: rec.cool_btu ?? null, sqft: rec.sqft ?? null, source: "typed", by: rec.by, at: rec.at, reading, reports };
  }
  if (withNums) {
    const l = aiLoad(withNums)!;
    return { heat: l.heat_loss_btuh ?? null, cool: l.heat_gain_btuh ?? null, sqft: l.area_sqft ?? null, source: "file", file: withNums, reading, reports };
  }
  return { heat: null, cool: null, sqft: null, source: "none", reading, reports };
}
export const loadRead = (f: PegasusFile) => aiLoad(f);

export function useSaveLoadCalc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { jobId: string; value: LoadCalcRecord | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const value = v.value ? { ...v.value, by: u?.user?.email ?? null, at: new Date().toISOString() } : null;
      const { error } = await supabase.from("pegasus_job").update({ load_calc: value, updated_at: new Date().toISOString() }).eq("id", v.jobId);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["job"] }); qc.invalidateQueries({ queryKey: ["jobs"] }); },
  });
}

/* ── The Equipment Check ──────────────────────────────────────────────────── */

/** The units that set the size — what a confirmation is tied to (add-ons and materials don't count). */
export const majorUnits = (units: JobEquipment[] | undefined) =>
  (units ?? []).filter((u) => u.kind !== "accessory" && u.kind !== "material");

/** A fingerprint of the major units: change one (swap, add, remove, qty) and the confirmation goes stale. */
export function equipSig(units: JobEquipment[] | undefined): string {
  return majorUnits(units)
    .map((u) => `${u.item_id ?? (u.model ?? u.part_number ?? "").trim().toUpperCase()}×${u.qty || 1}`)
    .sort()
    .join("|");
}

export interface SoldState {
  /** The stage is done: confirmed for the units on the job now (or the crew is past it). */
  done: boolean;
  confirmed: boolean;
  /** Confirmed, then a unit changed. */
  stale: boolean;
  /** A load calc is on file (or not needed, or Settings doesn't require one). */
  loadOk: boolean;
  next: "add_equipment" | "load_calc" | "confirm" | "reconfirm" | null;
  sig: string;
}

export function soldState(job: Job, units: JobEquipment[] | undefined, load: EffectiveLoad, rules: Pick<LoadCalcRules, "required">): SoldState {
  const sig = equipSig(units);
  const majors = majorUnits(units).length;
  const confirmed = !!job.equip_confirmed_at;
  const stale = confirmed && (job.equip_confirmed_sig ?? "") !== sig;
  const loadOk = load.source !== "none" || !rules.required;
  // Once the crew has finished (or the job closed), a swap on site doesn't reopen the sale.
  const past = !!job.crew_finished_at || job.status === "closed";
  const done = confirmed && (!stale || past);
  const next: SoldState["next"] = done ? null
    : majors === 0 ? "add_equipment"
    : !loadOk ? "load_calc"
    : stale ? "reconfirm"
    : "confirm";
  return { done, confirmed, stale, loadOk, next, sig };
}

export interface EquipCheckRecord {
  load: { heat: number | null; cool: number | null; source: EffectiveLoad["source"] };
  lines: { key: string; verdict: string; pct: number | null; capacity: number | null }[];
  units: string[];
}

export function useConfirmEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { jobId: string; sig: string | null; note?: string | null; check?: EquipCheckRecord | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const on = v.sig != null;
      const { error } = await supabase.from("pegasus_job").update({
        equip_confirmed_at: on ? new Date().toISOString() : null,
        equip_confirmed_by: on ? u?.user?.email ?? null : null,
        equip_confirmed_sig: on ? v.sig : null,
        equip_confirm_note: on ? (v.note ?? "").trim() || null : null,
        equip_check: on ? v.check ?? null : null,
        updated_at: new Date().toISOString(),
      }).eq("id", v.jobId);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["job"] }); qc.invalidateQueries({ queryKey: ["jobs"] }); },
  });
}

/* ── Pricebook lookups for the check ──────────────────────────────────────── */

export interface ItemSpec {
  id: string; description: string | null; efficiency: string | null; category: string | null; equip_type: string | null;
  model: string | null; part_number: string | null; manufacturer: string | null;
  average_cost: number | null; last_purchase: number | null; inactive: boolean;
}
const SPEC_COLS = "id, description, efficiency, category, equip_type, model, part_number, manufacturer, average_cost, last_purchase, inactive";

/** The pricebook rows behind these item ids (description + efficiency drive the sizes). */
export function useItemSpecs(ids: (string | null | undefined)[]) {
  const list = [...new Set(ids.filter(Boolean) as string[])].sort();
  return useQuery({
    queryKey: ["item_specs", list.join(",")],
    enabled: list.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Map<string, ItemSpec>> => {
      const { data, error } = await supabase.from("item").select(SPEC_COLS).in("id", list);
      if (error) throw error;
      return new Map(((data ?? []) as ItemSpec[]).map((r) => [r.id, r]));
    },
  });
}

/** Active pricebook items whose model starts with this series ("EL297UH", "EL16KC1-") — the other sizes. */
export function useSeriesItems(prefix: string | null) {
  const p = (prefix ?? "").replace(/[^A-Za-z0-9-]/g, "");
  return useQuery({
    queryKey: ["item_series", p],
    enabled: p.length >= 4,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<ItemSpec[]> => {
      const { data, error } = await supabase.from("item").select(SPEC_COLS)
        .or(`model.ilike.${p}%,part_number.ilike.${p}%,description.ilike.%${p}%`)
        .eq("inactive", false).eq("archived", false).limit(60);
      if (error) throw error;
      return (data ?? []) as ItemSpec[];
    },
  });
}

/** A stamped email → the person's first name from the team list ("seths@…" → "Seth"). */
export function usePersonName() {
  const { data } = useStaffNames();
  return (email: string | null | undefined) => (email ? staffFirst(data, email) : "");
}
